import React from 'react';
import PropTypes from 'prop-types';
import styled, { css, withTheme } from 'styled-components';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

// Import icons
import { ReactComponent as IconPlay } from 'assets/icons/ic_play.svg';

// Import styles
import { flex } from 'components/styles';

// Import components
import { Button, ProgramRecordings, AuthModal } from 'components/elements';
import Share from './share/Share';
import { Reminder } from '../../common';

// Import utilities
import { useAuth, useOperators } from 'components/utilities';

// Import helpers
import { sendGTMEvent } from 'helpers/tracking';

// Import variables
import {
	GTM_TRACKING_EVENTS,
	CLICK_GTM_ACTION,
	FILM_GTM_SEGMENTATION
} from 'helpers/tracking';
const { CLICK_TELEVISION } = GTM_TRACKING_EVENTS;

const PopoverToolbox = ({
	reminderId,
	linkDetails,
	programId,
	isUpcoming = false,
	isCatchup = false,
	isNotEnded = false,
	theme,
	startDate,
	channelId
}) => {
	const { pathname } = useLocation();
	const { data } = useSelector(({ devices }) => devices);
	const { lives: channels } = useSelector(({ channels }) => channels.data);
	const channel = channels.find((channel) => channel.uuid === channelId);
	const isNpvrActive = channel?.context?.npvr_active && isNotEnded;
	const isChannelAvailable = channel?.avaiable;

	const { isAuth } = useAuth();
	const { isDvrRecordingsOperator } = useOperators();

	const isDvrActive = isNotEnded && data.length > 0 && isDvrRecordingsOperator;
	const isRecordingEnabled = !!isNpvrActive || isDvrActive;
	const isRecordingsPanelVisible =
		isRecordingEnabled &&
		isAuth &&
		isChannelAvailable &&
		isDvrRecordingsOperator; // temporarily recordings not allowed on retail

	const handleButtonPlayClick = () =>
		sendGTMEvent({
			event: CLICK_TELEVISION,
			customEventData: {
				segmentation: FILM_GTM_SEGMENTATION,
				action: CLICK_GTM_ACTION,
				page_url: pathname,
				url: { url: linkDetails?.pathname },
				genres: channel?.genres,
				item_name: channel?.title
			}
		});

	const ButtonPlay = (
		<AuthModal>
			<Button as={Link} to={linkDetails} onClick={handleButtonPlayClick}>
				<IconPlayStyled />
			</Button>
		</AuthModal>
	);

	const reminderProps = { reminderId, theme, programId, startDate };
	return (
		<ToolBox>
			<>
				{isUpcoming && isAuth && (
					<Box>
						<Reminder {...reminderProps} />
					</Box>
				)}
				{!isUpcoming && isAuth && <Box isCatchup={isCatchup}>{ButtonPlay}</Box>}
				{isRecordingsPanelVisible && (
					<Box>
						<ProgramRecordings
							isDvrActive={isDvrActive}
							isNpvrActive={!!isNpvrActive}
							programId={programId}
						/>
					</Box>
				)}
			</>

			<Box>
				<Share linkDetails={linkDetails} />
			</Box>
		</ToolBox>
	);
};

const ToolBox = styled.div`
	height: 7rem;
	border-top: 1px solid ${({ theme: { popover } }) => popover.border};
	display: flex;
	justify-content: space-between;
`;

const Box = styled.div`
	flex: 1;
	${flex()}

	&:nth-child(2) {
		border-left: 1px solid ${({ theme: { popover } }) => popover.border};
		border-right: 1px solid ${({ theme: { popover } }) => popover.border};
	}

	svg {
		height: 3rem;
		width: 3rem;
	}

	${({ isCatchup }) =>
		isCatchup &&
		css`
			svg {
				g,
				path {
					fill: ${({ theme }) => theme.orange};
				}
			}
		`}
`;

const IconPlayStyled = styled(IconPlay)`
	g,
	path {
		fill: ${({ theme: { popover } }) => popover.icon};
	}
`;

PopoverToolbox.propTypes = {
	isUpcoming: PropTypes.bool.isRequired,
	programId: PropTypes.string.isRequired,
	linkDetails: PropTypes.shape({
		pathname: PropTypes.string,
		state: PropTypes.shape({ sCatchup: PropTypes.bool }),
		hash: PropTypes.string
	}),
	reminderId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([false])]),
	isCatchup: PropTypes.bool,
	isNotEnded: PropTypes.bool,
	startDate: PropTypes.string,
	channelId: PropTypes.string
};

export default withTheme(PopoverToolbox);
